
import React, { useState } from 'react';
import { useLanguage } from '@/context/language-context';
import { useAuth } from '@/context/auth';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  CreditCard, DollarSign, Receipt, Clock, ArrowUpRight, 
  ArrowDownRight, Calendar, Download, ChevronRight, Filter, Plus
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

// Données fictives pour le graphique de revenus
const revenueData = [
  { name: 'Jan', amount: 1200 },
  { name: 'Fév', amount: 1800 },
  { name: 'Mar', amount: 1400 },
  { name: 'Avr', amount: 2200 },
  { name: 'Mai', amount: 1800 },
  { name: 'Juin', amount: 2400 },
  { name: 'Juil', amount: 3000 },
];

// Données fictives pour les transactions
const transactionsData = [
  { 
    id: '1', 
    date: '2023-07-28T14:30:00Z', 
    type: 'incoming', 
    amount: 250, 
    description: 'Paiement client - Service standard', 
    status: 'completed' 
  },
  { 
    id: '2', 
    date: '2023-07-25T09:15:00Z', 
    type: 'incoming', 
    amount: 150, 
    description: 'Paiement client - Service express', 
    status: 'completed' 
  },
  { 
    id: '3', 
    date: '2023-07-22T18:45:00Z', 
    type: 'incoming', 
    amount: 350, 
    description: 'Paiement client - Service premium', 
    status: 'completed' 
  },
  { 
    id: '4', 
    date: '2023-07-20T12:00:00Z', 
    type: 'outgoing', 
    amount: 500, 
    description: 'Retrait vers compte bancaire', 
    status: 'completed' 
  },
  { 
    id: '5', 
    date: '2023-07-15T10:30:00Z', 
    type: 'incoming', 
    amount: 200, 
    description: 'Vente de contenu premium', 
    status: 'completed' 
  },
];

interface StatsCardProps {
  title: string;
  value: string;
  delta?: string;
  isDeltaPositive?: boolean;
  icon: React.ReactNode;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  delta,
  isDeltaPositive = true,
  icon,
}) => {
  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
            {icon}
          </div>
        </div>
        <div className="space-y-1">
          <h2 className="text-2xl font-bold">{value}</h2>
          {delta && (
            <p className={`text-xs flex items-center ${isDeltaPositive ? 'text-green-500' : 'text-red-500'}`}>
              {isDeltaPositive ? (
                <ArrowUpRight className="h-3 w-3 mr-1" />
              ) : (
                <ArrowDownRight className="h-3 w-3 mr-1" />
              )}
              {delta} depuis le mois dernier
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const EscortFinances: React.FC = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [period, setPeriod] = useState('month');
  
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold mb-2">{t('escort.finances.title')}</h1>
        <p className="text-muted-foreground">{t('escort.finances.description')}</p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title={t('escort.finances.total_earned')}
          value="€5,240.00"
          delta="12%"
          isDeltaPositive={true}
          icon={<DollarSign className="h-5 w-5" />}
        />
        <StatsCard
          title={t('escort.finances.pending_payments')}
          value="€350.00"
          icon={<Clock className="h-5 w-5" />}
        />
        <StatsCard
          title="Nombre de transactions"
          value="24"
          delta="5%"
          isDeltaPositive={true}
          icon={<CreditCard className="h-5 w-5" />}
        />
        <StatsCard
          title="Revenus mensuels moyens"
          value="€1,450.00"
          delta="8%"
          isDeltaPositive={true}
          icon={<Receipt className="h-5 w-5" />}
        />
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-4 mb-6">
          <TabsTrigger value="overview">{t('escort.dashboard.overview')}</TabsTrigger>
          <TabsTrigger value="transactions">{t('escort.finances.transactions')}</TabsTrigger>
          <TabsTrigger value="invoices">{t('escort.finances.invoices')}</TabsTrigger>
          <TabsTrigger value="payouts">{t('escort.finances.withdrawal')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>{t('escort.finances.revenue_chart')}</CardTitle>
                <CardDescription>
                  Aperçu de vos revenus pour la période sélectionnée
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Label htmlFor="period" className="sr-only">
                  {t('escort.finances.period')}
                </Label>
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('escort.finances.period')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="week">{t('escort.finances.this_week')}</SelectItem>
                    <SelectItem value="month">{t('escort.finances.this_month')}</SelectItem>
                    <SelectItem value="year">{t('escort.finances.this_year')}</SelectItem>
                    <SelectItem value="custom">{t('escort.finances.custom_range')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip formatter={(value) => [`€${value}`, 'Revenus']} />
                    <Bar dataKey="amount" fill="#8884d8" name="Revenus" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Dernières transactions</CardTitle>
                <CardDescription>
                  Les 5 dernières transactions effectuées
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {transactionsData.slice(0, 3).map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                          transaction.type === 'incoming' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                        }`}>
                          {transaction.type === 'incoming' ? (
                            <ArrowDownRight className="h-5 w-5" />
                          ) : (
                            <ArrowUpRight className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{transaction.description}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(transaction.date).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <p className={`font-medium ${
                        transaction.type === 'incoming' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'incoming' ? '+' : '-'}€{transaction.amount.toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t flex justify-center">
                  <Button variant="link" className="flex items-center">
                    Voir toutes les transactions <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Actions rapides</CardTitle>
                <CardDescription>
                  Accédez rapidement aux fonctionnalités financières
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <Button className="w-full justify-start" variant="outline">
                    <DollarSign className="h-4 w-4 mr-2" />
                    {t('escort.finances.request_withdrawal')}
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Receipt className="h-4 w-4 mr-2" />
                    {t('escort.finances.create_invoice')}
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Calendar className="h-4 w-4 mr-2" />
                    {t('escort.finances.generate_report')}
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger les relevés fiscaux
                  </Button>
                  <Button className="w-full justify-start" variant="outline">
                    <CreditCard className="h-4 w-4 mr-2" />
                    {t('escort.finances.add_payment_method')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="transactions">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle>{t('escort.finances.transactions')}</CardTitle>
                <CardDescription>
                  Historique de vos transactions
                </CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                <Button variant="outline" className="h-8 px-2 lg:px-3">
                  <Filter className="h-4 w-4 lg:mr-2" />
                  <span className="hidden lg:inline">Filtrer</span>
                </Button>
                <Button variant="outline" className="h-8 px-2 lg:px-3">
                  <Download className="h-4 w-4 lg:mr-2" />
                  <span className="hidden lg:inline">Exporter</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {transactionsData.length > 0 ? (
                <div className="rounded-md border">
                  <div className="grid grid-cols-5 bg-muted p-3 text-xs font-medium">
                    <div>Date</div>
                    <div>Description</div>
                    <div>Type</div>
                    <div>Montant</div>
                    <div>Statut</div>
                  </div>
                  <div>
                    {transactionsData.map((transaction) => (
                      <div key={transaction.id} className="grid grid-cols-5 p-3 text-sm border-t">
                        <div>{new Date(transaction.date).toLocaleDateString()}</div>
                        <div className="truncate">{transaction.description}</div>
                        <div>
                          {transaction.type === 'incoming' 
                            ? t('escort.finances.payment_received') 
                            : t('escort.finances.payment_sent')}
                        </div>
                        <div className={transaction.type === 'incoming' ? 'text-green-600' : 'text-red-600'}>
                          {transaction.type === 'incoming' ? '+' : '-'}€{transaction.amount.toFixed(2)}
                        </div>
                        <div>
                          <span className="bg-green-100 text-green-700 rounded-full px-2 py-0.5 text-xs">
                            {transaction.status === 'completed' ? 'Terminé' : 'En attente'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10">
                  <Receipt className="h-10 w-10 text-muted-foreground mb-3" />
                  <h3 className="text-lg font-medium mb-1">{t('escort.finances.no_transactions')}</h3>
                  <p className="text-sm text-muted-foreground text-center mb-4">
                    Les transactions apparaîtront ici une fois que vous commencerez à recevoir des paiements.
                  </p>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Enregistrer une transaction
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoices">
          <Card>
            <CardHeader>
              <CardTitle>{t('escort.finances.invoices')}</CardTitle>
              <CardDescription>
                Gérez vos factures et vos paiements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-10 text-muted-foreground">
                Cette fonctionnalité sera implémentée dans une prochaine mise à jour.
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts">
          <Card>
            <CardHeader>
              <CardTitle>{t('escort.finances.withdrawal')}</CardTitle>
              <CardDescription>
                Retirez vos fonds vers votre compte bancaire
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-10 text-muted-foreground">
                Cette fonctionnalité sera implémentée dans une prochaine mise à jour.
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EscortFinances;
